/** REZUMATUL DE BUSINESS CASE: ce se poate construi, cât costă, cât se vinde.
 *
 *  Structura e a fișei de calcul primite de la Stefan (24.09.2026): ipoteze,
 *  cantități derivate, costuri de dezvoltare, scenarii de vânzare, rezultate.
 *
 *  SOCOTEALA E A SERVERULUI. Până pe 30.10.2026 stătea aici, în browser; acum
 *  o face serviciul de planificare (`cost_model.business_case`), pe faptele pe
 *  care le adună control plane-ul (`api/business_case.py`): dosarul, soluțiile
 *  firului, documentul planului, devizele, lista de cantități, programul.
 *  Interfața primește rândurile cu cifrele și cheile textelor și le scrie în
 *  limba și moneda ecranului (`jetoane.ts`). Aici rămân TIPURILE pe care le
 *  desenează panourile și cele câteva constante ale ecranului «Ipoteze»; sursa
 *  lor de adevăr e motorul (`GET /planificare/business_case/implicite`).
 *
 *  FIECARE CIFRĂ ÎȘI SPUNE SURSA. Cerut anume: «for each section we need to
 *  have the source of where we got the information from so that nothing is
 *  unjustified». Sursa NU stă pe tabel — acolo se citesc numai numerele, ca în
 *  fișă — ci în panoul care se desface: de unde vine, cum iese, unde se schimbă.
 */
import { numar } from "../../i18n";

/** De unde știm o cifră. Ordinea contează: `proiect` bate `referinta`. */
export type Sursa =
  /** Din actele terenului — carte funciară, măsurătoare. */
  | "acte"
  /** Din regimul urbanistic al parcelei. */
  | "puz"
  /** Măsurat pe soluțiile ACESTUI proiect. */
  | "proiect"
  /** Cerut în tema de proiectare. */
  | "tema"
  /** Luat dintr-un proiect comparabil — nu e măsurat aici. */
  | "referinta"
  /** Calculat din celelalte. */
  | "formula"
  /** Scris de mână, fără altă acoperire. */
  | "ipoteza"
  /** O cotă stabilită prin lege (taxe, impozite). */
  | "lege";

/** IPOTEZELE: tot ce nu iese din calcul. Cheile sunt ale fișei; pe ele sunt
 *  scrise abaterile scenariilor păstrate pe server (`cost.business_case`).
 *  Valorile implicite și explicația fiecăreia: `cost_model/business_case/rezumat.py`. */
export type Ipoteze = {
  terenMp: number;
  cut: number;
  cedatDinScd: number;
  suPeGba: number;
  terasePeGba: number;
  comercialPeGba: number;
  subsolMp: number;
  mpPeLocParcare: number;
  apartamentMediuMp: number;
  verdeDinTeren: number;
  circulatiiDinTeren: number;
  pretTeraseMp: number;
  pretComercialMp: number;
  pretParcareLoc: number;
  /** Prețul apartamentelor, €/mp util — capătul care se mută pe scenarii. */
  pretApartamenteMp: number;
  /** Prețul terenului în bani, peste cota din SCD cedată în apartamente. */
  pretTerenBani: number;
  duratiAni: number;
  cotaImpozit: number;
  pretStructuraMp: number;
  pretArhitecturaMp: number;
  pretAnvelopaMp: number;
  pretSanitareMp: number;
  pretTermiceMp: number;
  pretElectriceMp: number;
  pretPsiMp: number;
  pretLifturiMp: number;
  pretSubsolSapaturaMp: number;
  pretSubsolSprijiniriMp: number;
  pretSubsolStructuraMp: number;
  pretSubsolHidroMp: number;
  pretSubsolInstalatiiMp: number;
  pretSubsolFinisajeMp: number;
  inaltimeSubsolM: number;
  fundatieSubGroapaM: number;
  pretSapaturaM3: number;
  factorPerimetru: number;
  infigereSprijinireM: number;
  pretSprijinireMp: number;
  pretVerdeMp: number;
  pretCirculatiiMp: number;
  infrastructuraMpScd: number;
  conexeMpScd: number;
  cadastruMpScd: number;
  costuriTeren: number;
  coteTaxe: number;
  coteManagement: number;
  coteComision: number;
  coteRezerva: number;
  /** soft costs as shares of the hard cost: site supervision + verification, CAR insurance */
  coteDirigentie: number;
  coteAsigurare: number;
  /** THE BANK LOAN: share of the cost without financing (loan-to-cost), the
   *  margin, «+ Euribor» (1 = ticked; deviations are numbers), Euribor 3M, the fee */
  creditDinCostPct: number;
  dobandaMarjaPct: number;
  dobandaPlusEuribor: number;
  euriborPct: number;
  comisionCreditPct: number;
  /** THE SALES: share cashed at completion, the months over which the rest sells */
  vandutLaReceptiePct: number;
  perioadaVanzareLuni: number;
  /** every scenario is read at three apartment prices: −band, chosen, +band */
  bandaPretPct: number;
  /** AJUSTĂRILE SCENARIULUI pe rândurile de cost, ca factor (1,1 = +10 %), după
   *  `Rand.cheie`. Un rând luat din devizul de execuție n-are un preț unitar
   *  de schimbat: «structura iese cu 10 % mai scumpă» se poate spune numai așa. */
  ajustari: Record<string, number>;
};

/** CE DECIDE DEZVOLTATORUL: singurele cifre ale rezumatului care nu se măsoară
 *  pe proiect și nu sunt repere de piață. Ecranul «Ipoteze» le cere confirmate
 *  înainte de rezultate; restul se schimbă acolo unde se nasc. (= `DECIZII` din motor) */
export const DECIZII = [
  "cedatDinScd", "pretTerenBani", "costuriTeren",
  "pretApartamenteMp",
  "pretTeraseMp", "pretComercialMp", "pretParcareLoc", "duratiAni",
  "creditDinCostPct", "dobandaMarjaPct", "comisionCreditPct", "vandutLaReceptiePct", "perioadaVanzareLuni",
] as const satisfies readonly (keyof Ipoteze)[];
export type Decizie = typeof DECIZII[number];

/** CE NU POATE SCHIMBA UN SCENARIU: cifrele care vin din proiect. Un scenariu
 *  cu alt teren ar fi alt proiect. (= `DIN_PROIECT` din motor) */
export const DIN_PROIECT = ["terenMp", "cut", "subsolMp"] as const satisfies readonly (keyof Ipoteze)[];
/** Ce vine din PROGRAMUL DE EXECUȚIE cât există unul: durata nu e o ipoteză, se
 *  schimbă prin program (macarale pe perioade, efective, lag-uri). (= `DIN_PROGRAM` din motor) */
export const DIN_PROGRAM = ["duratiAni"] as const satisfies readonly (keyof Ipoteze)[];

/** RÂNDURILE DE COST pe care un scenariu le poate ajusta cu un factor. (= `CHEI_AJUSTABILE` din motor) */
export const CHEI_AJUSTABILE = [
  "sapatura", "sprijiniri", "structura", "structuraSubsol", "arhitectura",
  "anvelopa", "sanitare", "termice", "electrice", "psi", "lifturi",
  "subsolHidro", "subsolInstalatii", "subsolFinisaje",
  "cVerde", "cCirc", "cInfra", "cConexe", "dirigentie", "asigurare", "cadastru",
] as const;

/** Ce se arată sub rând când se desface, în plus față de sursă: devizul de
 *  execuție pe articole. Îl desenează pagina care îl are (`Retete.tsx`). */
export type Detaliu = "terasamente" | "structura" | "fatada";

/** Un rând din rezumat: cifra, plus tot ce trebuie ca să fie justificată. */
export type Rand = {
  cheie: string;
  element: string;
  cantitate?: number;
  um?: string;
  /** Câte zecimale are cantitatea. */
  zecimale?: number;
  /** Cantitatea e un procent — se scrie cu `%`. */
  procent?: boolean;
  /** Cantitatea e o sumă în EUR — se scrie în moneda ecranului. */
  monetar?: boolean;
  parametru?: string;
  valoare?: number;
  /** Nota scurtă de lângă cifră — coloana «Observații» din fișă. */
  observatie?: string;
  sursa: Sursa;
  /** De unde vine, în cuvinte. */
  deUnde: string;
  /** Cum iese, cu cifrele puse în formulă. */
  cumIese: string;
  /** Neconcordanță între surse — pagina n-alege singură. */
  semnal?: string;
  /** Ecranul de unde se schimbă. */
  seSchimbaDin?: string;
  /** Eticheta grupului care începe cu rândul ăsta. */
  grup?: string;
  /** Suma rândurilor de deasupra — se citește, nu se adună încă o dată. */
  subtotal?: boolean;
  /** Devizul pe articole care se desface sub rând. */
  detaliu?: Detaliu;
  /** Cifra proiectului se calculează acum; până atunci, reperul. */
  inLucru?: boolean;
  /** Rând din listă cu o parte din valoare pe cantități estimate (sursa lui e tot `proiect`). */
  estimat?: boolean;
  /** Factorul pus de scenariu peste valoarea calculată (`Ipoteze.ajustari`). */
  ajustare?: number;
};

/** CE POARTĂ SEMNUL DE ESTIMAT: reperul din alt proiect, ipoteza scrisă de mână
 *  și rândul de listă cu cantități estimate. Totalurile nu: orice total al
 *  rezumatului conține măcar procentele de management, deci semnul n-ar mai
 *  deosebi nimic. */
export const eEstimat = (r: Rand) =>
  r.sursa === "referinta" || r.sursa === "ipoteza" || !!r.estimat;

export type Sectiune = { cheie: string; titlu: string; randuri: Rand[]; total?: Rand };

/** The summary's figures at one of the three apartment prices (`bandaPretPct`). */
export type PunctPret = {
  abatere: number; pret: number; baza: boolean;
  venituri: number; profitBrut: number; profitNet: number; marja: number; roi: number; roiPeAn: number;
  rir: number | null; prag: number; tic: number; capitalMax: number; dobanzi: number;
};

/** One month of the cash-flow: outflows, receipts, equity spent (cumulated), loan balance. */
export type LunaFlux = {
  luna: number; iesiri: number; intrari: number; capital_propriu: number; sold_credit: number; dobanda: number; sold: number;
};

export type Rezumat = {
  ipoteze: Ipoteze;
  sectiuni: Sectiune[];
  tic: number;
  ticPeMp: number;
  /** B · the hard cost (what `constructii` used to be) */
  constructii: number;
  land: number;
  hard: number;
  soft: number;
  /** the loan and the sales in time (`business_case/flux.py`) */
  credit: number;
  dobanzi: number;
  comisionCredit: number;
  rataAn: number;
  creditMax: number;
  primaTragere: number | null;
  rambursatLa: number | null;
  ultimaIncasare: number;
  capitalPropriu: number;
  fluxSerie: LunaFlux[];
  /** the summary at −band, chosen, +band apartment price; absent on a summary computed at another price */
  preturi?: PunctPret[];
  venituri: number;
  profitBrut: number;
  profitNet: number;
  marja: number;
  roi: number;
  roiPeAn: number;
  pragRentabilitate: number;
  /** banii în timp: capitalul maxim imobilizat, capital-anii, RIR; lipsă pe un rezumat vechi */
  capitalMax?: number | null;
  capitalAni?: number | null;
  rir?: number | null;
  fluxDinProgram?: boolean;
};

/** Durata în ani, cu jumătățile de an: un scenariu «+6 luni» are 3,5 ani, iar
 *  cu zero zecimale ar apărea «4 ani» deși ROI-ul pe an e împărțit la 3,5. */
export const ani = (v: number) => numar(v, { maximumFractionDigits: 1 });
